import { SDK_ERRORS } from '@riz/contracts/sdk';
import { warnOnce } from '../core/client.ts';
import type { SdkClient } from '../core/client.ts';

export const LEADERBOARD_SCOPES = ['global', 'country', 'friends'] as const;
export type LeaderboardScope = (typeof LEADERBOARD_SCOPES)[number];

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  score: number;
  plays: number;
}

export interface LeaderboardPage {
  game: string;
  scope: LeaderboardScope;
  season: string | null;
  entries: LeaderboardEntry[];
  you: LeaderboardEntry | null;
  /** Set when a scope is understood but has no data source yet. */
  unavailable?: string;
}

export interface LeaderboardSeason {
  id: string;
  label: string;
  opensAt: string;
  closesAt: string;
  current: boolean;
}

export interface LeaderboardQuery {
  limit?: number;
  scope?: LeaderboardScope;
  season?: 'current' | 'previous' | string;
}

export interface LeaderboardModule {
  get(query?: LeaderboardQuery): Promise<LeaderboardPage>;
  seasons(): Promise<LeaderboardSeason[]>;
}

export function createLeaderboardModule(client: SdkClient): LeaderboardModule {
  return {
    async get(query = {}) {
      const capability = client.requireCapability<LeaderboardPage>('leaderboard.read', 'Leaderboards');
      if (capability) return capability;

      const scope = normaliseScope(query.scope);
      if (scope !== 'global' && !client.capabilities.has('leaderboard.scopes')) {
        return unavailableScope(scope, 'Leaderboard scopes are not enabled for this game.');
      }
      if (query.season && !client.capabilities.has('leaderboard.seasons')) {
        return unavailableScope(scope, 'Leaderboard seasons are not enabled for this game.');
      }

      const result = await client.call('query', 'leaderboard', {
        limit: Math.max(1, Math.min(100, Number(query.limit) || 20)),
        scope,
        season: query.season ?? null,
      });

      // `friends` needs a social graph Riz does not have yet. Returning an empty
      // page with a stated reason lets a game ship the tab before the data
      // exists, which an error would not.
      if (typeof result.unavailable === 'string') {
        warnOnce(`scope-${scope}`, `Leaderboard scope "${scope}" is unavailable: ${result.unavailable}`);
      }

      return {
        game: String(result.game ?? ''),
        scope,
        season: (result.season as string | null) ?? null,
        entries: Array.isArray(result.entries) ? (result.entries as LeaderboardEntry[]) : [],
        you: (result.you as LeaderboardEntry | null) ?? null,
        unavailable: typeof result.unavailable === 'string' ? result.unavailable : undefined,
      };
    },

    async seasons() {
      const capability = client.requireCapability<LeaderboardSeason[]>('leaderboard.seasons', 'Leaderboard seasons');
      if (capability) return capability;
      const result = await client.call('query', 'leaderboard-seasons', {});
      return Array.isArray(result.seasons) ? (result.seasons as LeaderboardSeason[]) : [];
    },
  };
}

function normaliseScope(scope: LeaderboardScope | undefined): LeaderboardScope {
  return scope && (LEADERBOARD_SCOPES as readonly string[]).includes(scope) ? scope : 'global';
}

function unavailableScope(scope: LeaderboardScope, reason: string): Promise<LeaderboardPage> {
  warnOnce(`scope-${scope}-disabled`, reason);
  return Promise.resolve({
    game: '',
    scope,
    season: null,
    entries: [],
    you: null,
    unavailable: SDK_ERRORS.scopeUnavailable,
  });
}
